import { workspaceApiFetch } from '@affine/core/modules/cloud';
import { uploadWorkspaceBlob } from '@affine/core/modules/streamlain/workflow-viewer/widgets/upload-blob';
import type { AttachmentBlockModel } from '@blocksuite/affine/model';
import { GfxControllerIdentifier } from '@blocksuite/affine/std/gfx';
import { Bound, type SerializedXYWH } from '@blocksuite/global/gfx';
import { BlockSelection, type BlockStdScope } from '@blocksuite/std';

import type { SignedPdfVersionInfo } from '../../../desktop/dialogs/setting/workspace-setting/signatures/use-workspace-pdf-signatures';

const SIGNED_COPY_GAP = 24;

export interface SignedPdfCopyPlacementResult {
  blockId: string;
  sourceId: string;
  xywh: SerializedXYWH | null;
}

export async function fetchSignedPdfVersionFile({
  fileName,
  url,
  workspaceId,
}: {
  fileName: string;
  url: string;
  workspaceId: string;
}) {
  const response = await workspaceApiFetch(
    url,
    { credentials: 'include' },
    {
      workspaceId,
      category: 'foreground-action',
      mode: 'foreground',
      priority: 'foreground',
    }
  );
  if (!response.ok) {
    throw new Error(`Failed to download signed PDF: ${response.status}`);
  }
  const blob = await response.blob();
  return new File([blob], fileName, {
    type: blob.type || 'application/pdf',
  });
}

function getSourceBound(model: AttachmentBlockModel) {
  const xywh = model.props.xywh;
  if (typeof xywh !== 'string' || xywh.length === 0) return null;

  try {
    return Bound.deserialize(xywh as SerializedXYWH);
  } catch {
    return null;
  }
}

function getSignedCopyBound(model: AttachmentBlockModel) {
  const sourceBound = getSourceBound(model);
  if (!sourceBound) return null;

  return new Bound(
    sourceBound.x + sourceBound.w + SIGNED_COPY_GAP,
    sourceBound.y,
    sourceBound.w,
    sourceBound.h
  );
}

export async function insertSignedPdfCopyBesideSource({
  docId,
  file,
  signedVersion,
  sourceModel,
  std,
  workspaceId,
}: {
  docId: string;
  file: File;
  signedVersion: SignedPdfVersionInfo;
  sourceModel: AttachmentBlockModel;
  std?: BlockStdScope | null;
  workspaceId: string;
}): Promise<SignedPdfCopyPlacementResult> {
  const uploaded = await uploadWorkspaceBlob(workspaceId, file, { docId });
  if (uploaded.blobId !== signedVersion.signedBlobKey) {
    throw new Error('Signed PDF copy content did not match signed version');
  }
  const parentId = sourceModel.parent?.id;
  const targetBound = getSignedCopyBound(sourceModel);
  const attachmentProps = {
    embed: true,
    name: signedVersion.fileName || file.name,
    size: file.size,
    sourceId: uploaded.blobId,
    style: 'pdf',
    type: file.type || 'application/pdf',
  } as const;

  if (parentId && targetBound) {
    const xywh = targetBound.serialize();
    const blockId = sourceModel.store.addBlock(
      'affine:attachment' as never,
      {
        ...attachmentProps,
        xywh,
      },
      parentId
    );
    return { blockId, sourceId: uploaded.blobId, xywh };
  }

  if (!std) {
    throw new Error(
      'A BlockSuite std scope is required for page attachment insertion'
    );
  }

  const [blockId] = std.store.addSiblingBlocks(
    sourceModel,
    [
      {
        flavour: 'affine:attachment',
        ...attachmentProps,
      },
    ],
    'after'
  );
  if (!blockId) {
    throw new Error('Signed PDF copy attachment could not be inserted');
  }
  return { blockId, sourceId: uploaded.blobId, xywh: null };
}

export function focusSignedPdfCopyAttachment({
  blockId,
  std,
  xywh,
}: {
  blockId: string;
  std: BlockStdScope;
  xywh?: SerializedXYWH | null;
}) {
  const gfx = std.getOptional(GfxControllerIdentifier);
  if (gfx && xywh) {
    const bound = Bound.deserialize(xywh);
    gfx.selection.set({ elements: [blockId], editing: false });
    gfx.viewport.setCenter(bound.x + bound.w / 2, bound.y + bound.h / 2);
    return;
  }

  std.selection.setGroup('note', [
    std.selection.create(BlockSelection, { blockId }),
  ]);
}
